import { getIn } from 'formik';
import {
  ChosenOptionLabel,
  ChosenOptionsWrapper,
  Container,
  LeftPart,
  RightPart,
  Row,
} from './TableInput.styled';
import { IQuestion } from 'types/Question.types';
import Button from 'components/Button/Button';
import { setTableModalData } from 'store/TableModal/actions/tableModal';
import { useDispatch } from 'react-redux';
import { setTouchedQuestionName } from 'store/Form/actions/form';
import { useTranslation } from 'react-i18next';
import { useValidateTableInput } from './hooks';
import Icon from 'components/Icon/Icon';
import { Minus } from '@phosphor-icons/react';
import { COLORS } from 'assets/styled';
import { setPricing } from 'store/Model/actions/model';
import ClearValueButton from 'components/ClearValueButton/ClearValueButton';

interface ITableInputProps {
  pwId: string;
  question: IQuestion;
  context: any;
  isTouched: boolean;
  showClear?: boolean;
  reserveClear?: boolean;
  onClear?: () => void;
}

const TableInput = ({
  pwId,
  question,
  context,
  isTouched,
  showClear = false,
  reserveClear = false,
  onClear,
}: ITableInputProps) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const chosenOptions: any[] = getIn(context.values, question.initialKey);
  useValidateTableInput(question, context);

  const handleRemoveRow = (indexToRemove: number) => {
    if (!chosenOptions || chosenOptions.length === 0) {
      return;
    }

    // Filter out the row at the specified index
    const updatedOptions = chosenOptions.filter(
      (_, index) => index !== indexToRemove
    );

    // Update the field value with the filtered array
    // If no rows remain, set to undefined (empty state)
    context.setFieldValue(
      question.initialKey,
      updatedOptions.length > 0 ? updatedOptions : undefined
    );

    // Clear pricing because removing a row affects pricing
    dispatch(setPricing({}));
  };

  return (
    <Container>
      <Button
        label={t('Choose')}
        primary
        width="150rem"
        onClick={() => {
          dispatch(setTableModalData(question));
          if (!isTouched) {
            dispatch(setTouchedQuestionName(question.initialKey));
          }
        }}
      />
      <ChosenOptionsWrapper data-testid={pwId}>
        {chosenOptions
          ? chosenOptions.map(
              (chosenOption: any[], chosenOptionIndex: number) => (
                <Row key={chosenOptionIndex}>
                  <LeftPart>
                    <ChosenOptionLabel>{chosenOption[0]}</ChosenOptionLabel>
                  </LeftPart>
                  {question.multiple && (
                    <RightPart>
                      <Icon
                        tabIndex={'-1'}
                        svg={Minus}
                        weight={'bold'}
                        color={COLORS.RED_100}
                        onClick={() => handleRemoveRow(chosenOptionIndex)}
                      />
                    </RightPart>
                  )}
                </Row>
              )
            )
          : null}
      </ChosenOptionsWrapper>
      {(showClear || reserveClear) && onClear && (
        <ClearValueButton
          onClear={onClear}
          pwId={pwId + '_clear'}
          hidden={!showClear}
          // The chosen rows set the height here, so the button centres on them
          // rather than on a fixed control height, at the inset every other
          // control's clear button uses.
          alignCenter
        />
      )}
    </Container>
  );
};

export default TableInput;
